import { Button } from "@msuite/ui/button";
import { DashboardTile } from "@msuite/ui/dashboard";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { Tag } from "@msuite/ui/tag";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import {
	getAbsenceCategoryTranslationKey,
	getAbsenceStateColor,
	getAbsenceStateTranslationKey,
	getAbsenceTypeColor,
	getIsAbsenceEditable,
} from "@msuite/utils/absences";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { AbsenceListItem } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useAbsenceEditorRole } from "@/hooks/use-absence-editor-role";
import { useCitadel } from "@/hooks/use-citadel";
import { EditAbsenceModal } from "@/modals/users/edit-absence-modal";

/** Props Interface */
interface DashboardAbsenceItemProps {
	absence: AbsenceListItem;
}

export function DashboardAbsenceItem({ absence }: DashboardAbsenceItemProps) {
	/** Hooks */
	const context = DashboardTile.List.useContext();
	const { t } = useTranslation();
	const editAbsenceModalDisclosure = useDisclosure();
	const asyncModal = useAsyncModal();
	const { citadel } = useCitadel();
	const editorRole = useAbsenceEditorRole();

	const auth = useAuth();

	/** Functions */
	async function handleOnDeleteAbsence() {
		toast.promise(
			async () => {
				const isConfirmed = await asyncModal.confirm();
				if (!isConfirmed) return;
				const response = await client.users[":userId"].absences[":absenceId"].$delete({
					param: {
						userId: guardString(auth.user?.id),
						absenceId: absence.id,
					},
				});
				await responseHandler(response);
				await context.refetch();
			},
			{
				loading: t("general.deleting"),
				success: t("general.successfully_deleted"),
			},
		);
	}

	/** Computed */
	// The dashboard tile shows the caller's own leave, and the delete route
	// checks `delete Absence` for it — a rule a member holds for their own
	// requests, and a role can take away — so the entry follows the rule.
	const canDeleteAbsence = citadel?.can("delete", "Absence") === true;

	/** Render */
	return (
		<React.Fragment>
			<DashboardTile.List.Item key={absence.id}>
				<div
					className="grid gap-4 w-full items-center"
					style={{
						gridTemplateColumns: "1fr max-content",
					}}
				>
					<div className="v-stack gap-0 items-stretch">
						<div className="h-stack justify-between">
							<Tag
								color={getAbsenceTypeColor(
									absence.category as Parameters<typeof getAbsenceTypeColor>[0],
								)}
								variant="plain"
							>
								{t(getAbsenceCategoryTranslationKey(absence.category))}
							</Tag>
							<Tag
								color={getAbsenceStateColor(absence.state)}
								variant="plain"
							>
								{t(getAbsenceStateTranslationKey(absence.state))}
							</Tag>
						</div>
						<p className="text-left">
							{moment().getDateSpanString([absence.startDate, absence.endDate])}
						</p>
					</div>
					<Menu
						items={createStandardMenuItems({
							onEdit: editAbsenceModalDisclosure.onOpen,
							onDelete: canDeleteAbsence ? handleOnDeleteAbsence : undefined,
							onEditOptions: {
								isDisabled: !getIsAbsenceEditable(absence, editorRole),
							},
							onDeleteOptions: {
								isDisabled: !getIsAbsenceEditable(absence, editorRole),
							},
						})}
					>
						<Button
							icon={<Icon name="EllipsisVertical" />}
							variant="ghost"
							size="xs"
						/>
					</Menu>
				</div>
			</DashboardTile.List.Item>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("absences.delete_absences_confirm_message")}
				/>
			)}
			{editAbsenceModalDisclosure.isOpen && (
				<EditAbsenceModal
					isOpen={editAbsenceModalDisclosure.isOpen}
					onClose={editAbsenceModalDisclosure.onClose}
					absenceId={absence.id}
				/>
			)}
		</React.Fragment>
	);
}
